import React, { useState } from "react";
import {
  ChevronUp,
  ListFilter,
  Link2,
  User,
  Users,
  CalendarDays,
  Clock,
  MapPin,
  Building,
  Building2,
  Globe,
  GitCompareArrows,
  Search,
  ShieldAlert,
  Coins,
  Package,
  Award,
  Flag,
  Landmark,
  Briefcase,
  Info,
  Activity,
  Crosshair,
  FileText,
  Scale,
} from "lucide-react";
import { safeIcon } from "../config/graphIcons";

const normalizeIconKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const ICON_ALIASES = {
  person: User,
  official: User,
  politician: User,
  legislators: User,
  personnel: Users,
  stakeholder: Users,
  group: Users,
  "population group": Users,

  event: CalendarDays,
  datetime: Clock,
  date: CalendarDays,
  "conflict phase": CalendarDays,
  "term length": Clock,
  "election pattern": CalendarDays,

  country: MapPin,
  region: MapPin,
  location: MapPin,
  territories: MapPin,
  "administrative division": MapPin,

  organization: Building,
  company: Building,
  "government body": Landmark,
  "news outlet": Globe,
  "diplomatic facility": Building2,
  "legislative body": Landmark,

  action: GitCompareArrows,
  initiative: Activity,
  directive: FileText,
  inquiry: Search,
  service: Package,
  entity: Info,

  conflict: ShieldAlert,
  "military operation": ShieldAlert,
  "target group": Crosshair,

  market: Coins,
  commodity: Coins,
  cost: Coins,

  flag: Flag,
  document: FileText,
  occupation: Briefcase,
  court: Scale,
};

const getLegendIcon = (type) => {
  const normalized = normalizeIconKey(type);
  return ICON_ALIASES[normalized] || safeIcon(type) || Info;
};

const LegendPanel = ({
  dark,
  t,
  typeCounts,
  linkTypeCounts = [],
  activeTypeFilter,
  setActiveTypeFilter,
  activeLinkTypeFilter,
  setActiveLinkTypeFilter,
}) => {
  const [open, setOpen] = useState(true);
  const [mode, setMode] = useState("nodes");

  const nodeCount = typeCounts.length;
  const linkCount = linkTypeCounts.length;

  if (!nodeCount && !linkCount) return null;

  const activeNodeCount = activeTypeFilter.size;
  const activeLinkCount = activeLinkTypeFilter.size;
  const activeCount = activeNodeCount + activeLinkCount;

  const isNodesMode = mode === "nodes";
  const currentItems = isNodesMode ? typeCounts : linkTypeCounts;
  const currentActiveCount = isNodesMode ? activeNodeCount : activeLinkCount;

  const canClearCurrent = currentActiveCount > 0;
  const canClearAll = activeCount > 0;

  const panel = {
    background: dark ? "rgba(10,16,28,0.72)" : "rgba(255,255,255,0.78)",
    border: dark
      ? "1px solid rgba(148,163,184,0.14)"
      : "1px solid rgba(100,116,139,0.18)",
    boxShadow: dark
      ? "0 18px 48px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.04)"
      : "0 18px 42px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.70)",
  };

  const text = {
    primary: dark ? "rgba(226,232,240,0.90)" : "rgba(15,23,42,0.88)",
    muted: dark ? "rgba(148,163,184,0.62)" : "rgba(71,85,105,0.68)",
    faint: dark ? "rgba(148,163,184,0.35)" : "rgba(71,85,105,0.42)",
    accent: dark ? "rgba(56,189,248,0.92)" : "rgba(2,132,199,0.95)",
    danger: dark ? "rgba(248,113,113,0.82)" : "rgba(220,38,38,0.82)",
  };

  const activeBg = dark ? "rgba(14,165,233,0.12)" : "rgba(14,165,233,0.09)";
  const inactiveHover = dark
    ? "rgba(148,163,184,0.06)"
    : "rgba(100,116,139,0.07)";

  const disabledButtonStyle = {
    color: text.faint,
    cursor: "not-allowed",
    opacity: 0.58,
  };

  const clearCurrent = () => {
    if (!canClearCurrent) return;

    if (isNodesMode) setActiveTypeFilter(new Set());
    else setActiveLinkTypeFilter(new Set());
  };

  const clearAll = () => {
    if (!canClearAll) return;

    setActiveTypeFilter(new Set());
    setActiveLinkTypeFilter(new Set());
  };

  return (
    <div
      className="pointer-events-auto absolute left-4 top-[76px] z-20 w-[236px] overflow-hidden rounded-xl backdrop-blur-xl"
      style={panel}
    >
      <button
        className="flex w-full cursor-pointer items-center justify-between px-3.5 py-2.5 text-left transition-colors"
        style={{
          borderBottom: open
            ? dark
              ? "1px solid rgba(148,163,184,0.10)"
              : "1px solid rgba(100,116,139,0.12)"
            : "1px solid transparent",
        }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className="flex min-w-0 items-center gap-2">
          <div
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg"
            style={{
              background: dark
                ? "rgba(14,165,233,0.10)"
                : "rgba(14,165,233,0.08)",
              border: dark
                ? "1px solid rgba(14,165,233,0.18)"
                : "1px solid rgba(14,165,233,0.14)",
            }}
          >
            <ListFilter size={13} style={{ color: text.accent }} />
          </div>

          <div className="min-w-0">
            <div
              className="truncate text-[11px] font-medium uppercase tracking-[0.11em]"
              style={{ color: text.primary }}
            >
              Filters
            </div>
            <div className="text-[10px]" style={{ color: text.faint }}>
              {nodeCount} node types · {linkCount} link types
            </div>
          </div>
        </div>

        <ChevronUp
          size={14}
          style={{
            color: text.muted,
            transform: open ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 0.18s ease",
          }}
        />
      </button>

      {open && (
        <>
          <div
            className="grid grid-cols-2 gap-1 px-2 py-2"
            style={{
              borderBottom: dark
                ? "1px solid rgba(148,163,184,0.08)"
                : "1px solid rgba(100,116,139,0.10)",
            }}
          >
            <button
              className="rounded-lg px-2 py-1.5 text-[11px] transition-colors"
              style={{
                background: mode === "nodes" ? activeBg : "transparent",
                color: mode === "nodes" ? text.accent : text.muted,
                border:
                  mode === "nodes"
                    ? dark
                      ? "1px solid rgba(14,165,233,0.18)"
                      : "1px solid rgba(14,165,233,0.14)"
                    : "1px solid transparent",
              }}
              onClick={() => setMode("nodes")}
            >
              Nodes
              {activeNodeCount > 0 ? ` (${activeNodeCount})` : ""}
            </button>

            <button
              className="rounded-lg px-2 py-1.5 text-[11px] transition-colors"
              style={{
                background: mode === "links" ? activeBg : "transparent",
                color: mode === "links" ? text.accent : text.muted,
                border:
                  mode === "links"
                    ? dark
                      ? "1px solid rgba(14,165,233,0.18)"
                      : "1px solid rgba(14,165,233,0.14)"
                    : "1px solid transparent",
              }}
              onClick={() => setMode("links")}
            >
              Links
              {activeLinkCount > 0 ? ` (${activeLinkCount})` : ""}
            </button>
          </div>

          <div
            className="relative max-h-[min(420px,52vh)] overflow-y-auto px-1.5 py-1.5"
            style={{ scrollbarWidth: "thin" }}
          >
            {currentItems.map(([type, count]) => {
              const isNode = mode === "nodes";
              const isActive = isNode
                ? activeTypeFilter.has(type)
                : activeLinkTypeFilter.has(type);

              const IconComp = isNode ? getLegendIcon(type) : Link2;
              const normalizedType = normalizeIconKey(type);
              const iconColor = isNode
                ? t.node.typeColors?.[type] ||
                  t.node.typeColors?.[normalizedType] ||
                  t.node.iconColor
                : text.accent;

              return (
                <button
                  key={`${mode}-${type}`}
                  className="group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-all"
                  style={{
                    background: isActive ? activeBg : "transparent",
                    border: isActive
                      ? dark
                        ? "1px solid rgba(14,165,233,0.20)"
                        : "1px solid rgba(14,165,233,0.16)"
                      : "1px solid transparent",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = inactiveHover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                  onClick={() => {
                    if (isNode) {
                      setActiveTypeFilter((prev) => {
                        const next = new Set(prev);
                        if (next.has(type)) next.delete(type);
                        else next.add(type);
                        return next;
                      });
                    } else {
                      setActiveLinkTypeFilter((prev) => {
                        const next = new Set(prev);
                        if (next.has(type)) next.delete(type);
                        else next.add(type);
                        return next;
                      });
                    }
                  }}
                >
                  <div
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors"
                    style={{
                      background: isActive
                        ? dark
                          ? "rgba(14,165,233,0.14)"
                          : "rgba(14,165,233,0.10)"
                        : dark
                          ? "rgba(148,163,184,0.06)"
                          : "rgba(100,116,139,0.06)",
                    }}
                  >
                    {React.createElement(IconComp, {
                      size: 13,
                      strokeWidth: 1.9,
                      style: {
                        color: isActive ? text.accent : iconColor,
                        opacity: isActive ? 1 : 0.78,
                        flexShrink: 0,
                      },
                    })}
                  </div>

                  <span
                    className="min-w-0 flex-1 truncate text-[12px] font-normal"
                    style={{ color: isActive ? text.primary : text.muted }}
                  >
                    {type.replace(/_/g, " ").toLowerCase()}
                  </span>

                  <span
                    className="shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium tabular-nums"
                    style={{
                      color: isActive ? text.accent : text.faint,
                      background: isActive
                        ? dark
                          ? "rgba(14,165,233,0.10)"
                          : "rgba(14,165,233,0.08)"
                        : "transparent",
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}

            {currentItems.length === 0 && (
              <div
                className="px-3 py-6 text-center text-[11px]"
                style={{ color: text.faint }}
              >
                No {mode === "nodes" ? "node" : "link"} types
              </div>
            )}
          </div>

          <div
            className="grid grid-cols-2"
            style={{
              borderTop: dark
                ? "1px solid rgba(148,163,184,0.10)"
                : "1px solid rgba(100,116,139,0.12)",
            }}
          >
            <button
              disabled={!canClearCurrent}
              className="px-3 py-2 text-center text-[11px] font-normal transition-colors disabled:pointer-events-none"
              style={{
                color: canClearCurrent ? text.accent : text.faint,
                background: "transparent",
                borderRight: dark
                  ? "1px solid rgba(148,163,184,0.10)"
                  : "1px solid rgba(100,116,139,0.12)",
                cursor: canClearCurrent ? "pointer" : "not-allowed",
                opacity: canClearCurrent ? 1 : disabledButtonStyle.opacity,
              }}
              onClick={clearCurrent}
            >
              Clear {mode}
            </button>

            <button
              disabled={!canClearAll}
              className="px-3 py-2 text-center text-[11px] font-normal transition-colors disabled:pointer-events-none"
              style={{
                color: canClearAll ? text.danger : text.faint,
                background: "transparent",
                cursor: canClearAll ? "pointer" : "not-allowed",
                opacity: canClearAll ? 1 : disabledButtonStyle.opacity,
              }}
              onClick={clearAll}
            >
              Clear all
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default LegendPanel;
